import { useCallback, useState } from "react"
import type { MouseEvent } from "react"
import { VisibilityOutlined } from "@mui/icons-material"
import { Alert, Box, Button, Card, Chip, CircularProgress, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { useAddSkillProposal } from "resources/skill-proposals"
import { ConfirmActionDialog } from "./confirm-action-dialog"
import { skillPackageArtifact } from "./skill-package-artifact"
import { useArtifactPanelActions } from "./use-artifact-panel"
import type { SkillProposal } from "./skill-proposal-contract"

const TINT = "color-mix(in srgb, var(--app-primary) 7%, transparent)"
const EDGE = "color-mix(in srgb, var(--app-primary) 55%, transparent)"

const CARD_SX = ((theme: Theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
  p: 2,
  mt: 1.5,
  borderRadius: 2,
  bgcolor: TINT,
  borderColor: EDGE,
  ...theme.applyStyles("dark", { backgroundColor: TINT, borderColor: EDGE }),
})) satisfies SxProps<Theme>
const HEAD_SX = { display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" } satisfies SxProps<Theme>
const NAME_SX = { fontWeight: 600, minWidth: 0, overflowWrap: "anywhere" } satisfies SxProps<Theme>
const DESCRIPTION_SX = {
  color: "text.secondary", overflow: "hidden", overflowWrap: "anywhere",
  display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 1,
} satisfies SxProps<Theme>
const ACTIONS_SX = { display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" } satisfies SxProps<Theme>
const PREVIEW_SX = { ml: "auto", flexShrink: 0 } satisfies SxProps<Theme>

export interface SkillProposalCardProps {
  proposal: SkillProposal
}

export function SkillProposalCard({ proposal }: SkillProposalCardProps) {
  const { add, isAdding, failure, added } = useAddSkillProposal()
  const [isReplaceDialogOpen, setIsReplaceDialogOpen] = useState(false)
  const panel = useArtifactPanelActions()
  const isReplace = proposal.mode === "replace"
  const isExpired = proposal.bundleState !== "available"

  const onPreview = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    const artifact = skillPackageArtifact(proposal)
    if (artifact === null) return
    panel?.openArtifact(artifact, event.currentTarget, proposal.name)
  }, [panel, proposal])

  const runAdd = useCallback(() => {
    setIsReplaceDialogOpen(false)
    void add(proposal)
  }, [add, proposal])

  const runAddAsNew = useCallback(() => {
    void add(proposal, { asNew: true })
  }, [add, proposal])

  const onPrimaryAction = useCallback(() => {
    if (isReplace) setIsReplaceDialogOpen(true)
    else runAdd()
  }, [isReplace, runAdd])

  const closeReplaceDialog = useCallback(() => setIsReplaceDialogOpen(false), [])

  return (
    <Card variant="outlined" sx={CARD_SX} data-testid="kelly-skill-proposal">
      <Box sx={HEAD_SX}>
        <Typography sx={NAME_SX} data-testid="kelly-skill-proposal-name">{proposal.name}</Typography>
        {/* The one thing a create and a replace must not look alike over. The action label says "replace" too,
            and the confirm dialog names the skill before anything commits — but both come after the user has
            decided to engage, and this is what makes the two cards differ at a glance. */}
        {isReplace && <Chip size="small" color="warning" variant="outlined" label="Replaces an existing skill" data-testid="kelly-skill-proposal-replace" />}
        {/* Only where there is a panel to open, and only while the package still exists — an aged-out
            proposal has no bytes, so Preview is withheld exactly as Add is. */}
        {panel !== null && !isExpired && (
          <Button
            size="small"
            variant="text"
            sx={PREVIEW_SX}
            startIcon={<VisibilityOutlined fontSize="small" />}
            onClick={onPreview}
            data-testid="kelly-skill-proposal-preview"
          >
            Preview contents
          </Button>
        )}
      </Box>
      {proposal.description !== "" && (
        <Typography variant="body2" sx={DESCRIPTION_SX} data-testid="kelly-skill-proposal-description">{proposal.description}</Typography>
      )}
      {isExpired ? (
        <Alert severity="info" data-testid="kelly-skill-proposal-bundle-expired">
          {`Kelly's copy of "${proposal.name}" wasn't kept this long, so it can't be added now. Ask Kelly to build it again and you can add it straight away.`}
        </Alert>
      ) : added !== null ? (
        <Alert severity="success" data-testid="kelly-skill-proposal-added">
          {`"${added.name}" is in the Skills pane, under Yours.`}
        </Alert>
      ) : failure?.kind === "replaceTargetGone" ? (
        <Box sx={ACTIONS_SX} data-testid="kelly-skill-proposal-replace-target-gone">
          <Alert severity="warning">
            {failure.message ?? `The skill "${proposal.name}" would have replaced no longer exists.`}
          </Alert>
          <Button variant="contained" size="small" disabled={isAdding} onClick={runAddAsNew} data-testid="kelly-skill-proposal-add-as-new">
            Add as a new skill
          </Button>
        </Box>
      ) : failure?.kind === "nameTaken" ? (
        <Alert severity="warning" data-testid="kelly-skill-proposal-name-taken">
          {failure.message ?? `A skill named "${proposal.name}" already exists. Ask Kelly to rename it.`}
        </Alert>
      ) : failure?.kind === "rejected" ? (
        <Alert severity="warning" data-testid="kelly-skill-proposal-rejected">
          {failure.message ?? `"${proposal.name}" cannot be added as it stands. Ask Kelly to revise it.`}
        </Alert>
      ) : failure?.kind === "tooLarge" ? (
        <Alert severity="warning" data-testid="kelly-skill-proposal-too-large">
          {`"${proposal.name}" is too large to add. Ask Kelly to trim it.`}
        </Alert>
      ) : (
        <>
          {failure?.kind === "retryable" && (
            <Alert severity="error" data-testid="kelly-skill-proposal-error">
              {`"${proposal.name}" was not saved. Try adding it again.`}
            </Alert>
          )}
          <Box sx={ACTIONS_SX}>
            <Button
              variant="contained"
              size="small"
              disabled={isAdding}
              onClick={onPrimaryAction}
              data-testid="kelly-skill-proposal-confirm"
              startIcon={isAdding ? <CircularProgress size={14} /> : undefined}
            >
              {isReplace ? "Review and replace" : "Add to my skills"}
            </Button>
          </Box>
        </>
      )}

      <ConfirmActionDialog
        open={isReplaceDialogOpen}
        title={`Replace "${proposal.name}"?`}
        body={`You already have a skill called "${proposal.name}". Confirming replaces its contents with this version. Whether it is switched on for Kelly stays as you have it.`}
        confirmLabel="Replace"
        onClose={closeReplaceDialog}
        onConfirm={runAdd}
      />
    </Card>
  )
}
